import axios from 'axios';
import { API_CONFIG, ERROR_CODES } from './apiConfig';
import storageService from './storageService'; // Secure storage

/**
 * Cliente API centralizado con interceptores
 * Maneja autenticación JWT con refresh automático
 */
const apiClient = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: API_CONFIG.headers,
    // Solo usar cookies en producción (cuando el backend de auth esté listo)
    withCredentials: process.env.NODE_ENV === 'production'
});

// ==========================================
// Request Interceptor
// ==========================================
apiClient.interceptors.request.use(
    (config) => {
        // Inyectar Token Bearer si existe
        const token = storageService.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Log para desarrollo
        if (process.env.NODE_ENV === 'development') {
            console.log('API Request:', config.method?.toUpperCase(), config.url);
        }

        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

// ==========================================
// Response Interceptor
// ==========================================
apiClient.interceptors.response.use(
    (response) => {
        // Log para desarrollo
        if (process.env.NODE_ENV === 'development') {
            console.log('API Response:', response.status, response.config.url);
        }

        return response.data;
    },
    async (error) => {
        const originalRequest = error.config;

        // Manejo de errores
        const errorResponse = {
            message: 'Error desconocido',
            code: ERROR_CODES.NETWORK_ERROR,
            details: null
        };

        if (error.response) {
            // El servidor respondió con un código de error
            errorResponse.code = error.response.status;
            errorResponse.message = error.response.data?.message || getErrorMessage(error.response.status);
            errorResponse.details = error.response.data;

            // Manejo específico de 401 (Token expirado)
            if (error.response.status === ERROR_CODES.UNAUTHORIZED && !originalRequest._retry) {
                // Evitar refresh en endpoints de autenticación
                if (originalRequest.url.includes('/auth/login') ||
                    originalRequest.url.includes('/auth/refresh')) {
                    console.error('API Error:', errorResponse);
                    return Promise.reject(errorResponse);
                }

                console.error('API Error: 401 Unauthorized', errorResponse);

                // Si la sesión expiró (401), forzar logout limpiando storage
                if (typeof window !== 'undefined') {
                    // Evitar loop infinito si ya estamos en login
                    if (!window.location.pathname.includes('/login')) {
                        console.warn('Sesión expirada. Redirigiendo a login...');
                        // No podemos importar AuthContext aquí, pero limpiar storage es seguro
                        storageService.clear();
                        window.location.href = '/login';
                    }
                }

                return Promise.reject(errorResponse);
            }

            // Manejo específico de otros errores
            switch (error.response.status) {
                case ERROR_CODES.FORBIDDEN:
                    errorResponse.message = 'No tienes permisos para realizar esta acción';
                    break;

                case ERROR_CODES.NOT_FOUND:
                    errorResponse.message = 'Recurso no encontrado';
                    break;

                case ERROR_CODES.SERVER_ERROR:
                    if (!error.response.data?.message) {
                        errorResponse.message = 'Error del servidor. Por favor, intenta más tarde';
                    }
                    break;

                default:
                    break;
            }
        } else if (error.request) {
            // La petición se hizo pero no hubo respuesta
            errorResponse.message = 'No se pudo conectar con el servidor';
            errorResponse.code = ERROR_CODES.NETWORK_ERROR;
        } else {
            // Error al configurar la petición
            errorResponse.message = error.message;
        }

        console.error('API Error:', errorResponse);
        return Promise.reject(errorResponse);
    }
);

// ==========================================
// Helper Functions
// ==========================================
function getErrorMessage(statusCode) {
    const messages = {
        400: 'Solicitud inválida',
        401: 'No autorizado',
        403: 'Acceso prohibido',
        404: 'No encontrado',
        500: 'Error del servidor',
        502: 'Bad Gateway',
        503: 'Servicio no disponible'
    };

    return messages[statusCode] || 'Error en la solicitud';
}

export default apiClient;
